// Shared mechanics for the portaled builder node visual controls.
//
// Each node's `.node-visual-controls` element is reparented out of its node into a
// single overlay (inside `.builder-doc-wrapper`) so it escapes every node's stacking
// context / transformed containing block. Once portaled, the controls are no longer a
// descendant of their
// node, so the usual `closest()` / descendant `querySelector` relationships break.
// These helpers re-establish the node <-> controls mapping by `data-node-id` instead,
// and own the overlay + reparenting. They're stateless and frame-agnostic (they
// resolve within the element's own `ownerDocument`), so the parent-frame node tree
// can reuse them against the preview iframe doc too.

export const OVERLAY_CLASS = 'js-node-visual-controls-overlay'

// The doc-wrapper of the content being EDITED — not the layout's. A page rendered inside a layout
// has TWO `.builder-doc-wrapper` elements in the preview iframe: the layout's (outer, non-editing,
// rendered in a secondary context so it carries no `builder--editor--doc-wrapper` controller) and
// the content's (inner, editing, which carries it and hosts the node-controls overlay). A bare
// `querySelector('.builder-doc-wrapper')` returns the OUTER layout wrapper first, so the overlay
// host would key off the wrong one — placing the toolbar-action overlay outside the
// `builder--editor--doc-wrapper` controller that those actions resolve against. Select the editing
// wrapper explicitly; fall back to the first wrapper for the no-layout case (there's only one, and
// it's the editing one).
export const editingDocWrapper = (doc) =>
  doc.querySelector('.builder-doc-wrapper[data-builder-doc-editing="true"]') ||
  doc.querySelector('.builder-doc-wrapper')

// Host for the page overlay: inside `.builder-doc-wrapper` (falling back to <body>). It must
// be OUTSIDE every node (so node stacking contexts / transforms can't re-trap the
// controls) but INSIDE the doc-wrapper — the toolbar buttons fire `doc_wrapper_action`s
// that resolve to the `builder--editor--doc-wrapper` Stimulus controller mounted on
// `.builder-doc-wrapper`, and Stimulus only binds an action when its controller is an
// ANCESTOR of the action element. A body-level overlay sits outside that scope, so those
// actions (copy/delete/duplicate/edit/turn-into/add/…) silently do nothing.
const overlayHost = (doc) => editingDocWrapper(doc) || doc.body

// Create the shared overlay div inside `host`. The `tw` class re-establishes the Tailwind
// scope the controls' utilities need. Children are positioned relative to this overlay's own
// box (see measureControlsPosition), so the mount point can move freely without affecting
// placement.
//
// Pin `--tw-base-font-size` to 16px so the portaled chrome (level pills, proximity adder)
// sizes as fixed editor UI. Our Tailwind font-size scale compiles to
// `calc(var(--tw-base-font-size, 16px) * N)`, and the overlay lives inside
// `.builder-doc-wrapper` where the style guide sets `--tw-base-font-size` to the edited page's
// base font size (`calc(1rem * …)`). Without this reset the pills/adders scale with the page
// font instead of staying editor-chrome sized — `.node-visual-controls` pins the same 16px for
// the same reason, but these were portaled out of it. `--tw-line-height-factor` is pinned
// alongside it for the same reason: every text-* and leading-* utility multiplies it in, as
// every gap-* utility does `--tw-gap-factor`.
const makeOverlay = (host, cssText) => {
  const overlay = host.ownerDocument.createElement('div')
  overlay.className = `${OVERLAY_CLASS} tw`
  // Editor chrome follows the admin theme (see admin_theme_sync.js, which keeps this in step).
  if (host.ownerDocument.documentElement.dataset.colorMode === 'dark') {
    overlay.classList.add('dark')
  }
  overlay.style.cssText = cssText
  overlay.style.setProperty('--tw-base-font-size', '16px')
  overlay.style.setProperty('--tw-line-height-factor', '1')
  overlay.style.setProperty('--tw-gap-factor', '1')
  host.appendChild(overlay)
  return overlay
}

// The page overlay holding portaled controls for page (non-popup) content. Also the home of
// the level-selector pills and proximity-adder (which are always page-level chrome), so this
// signature stays doc-based for those callers. No z-index: page frames rely on their own
// z-300 to sit above gap controls (z-200) within the doc-wrapper's stacking context.
// The overlay already inside `host`, if any.
//
// NOT `host.querySelector(':scope > .X')`. That reads as a child-only lookup, but Chrome runs it as
// a document-order walk of the host's WHOLE subtree, and makeOverlay appends the overlay, so every
// call walked everything before reaching it. On the 673-element page the doc-wrapper has FIVE
// direct children and a 15,043-element subtree: measured over 667 calls, 62.4 ms as the query
// against 0.1 ms as this loop. getControlsOverlay runs on every portalize and on every mousemove
// that positions the proximity adder, so this is a hot path, not a micro-optimisation.
//
// Forward, matching `:scope >`'s first-match semantics exactly rather than merely equivalently —
// with a handful of children there is nothing to win by scanning from the end.
const existingOverlay = (host) => {
  for (const child of host.children) {
    if (child.classList.contains(OVERLAY_CLASS)) return child
  }
  return null
}

export const getControlsOverlay = (doc = document) => {
  const host = overlayHost(doc)
  return (
    existingOverlay(host) ||
    makeOverlay(host, 'position:absolute;top:0;left:0;')
  )
}

// The overlay that should host `node`'s portaled controls. Popup-content controls go into a
// second overlay INSIDE the popup's own fixed modal layer (`.t-modal-content-outer`, z-[99999])
// so they share the modal's stacking context and paint above the popup content. A doc-wrapper
// overlay sits in a different stacking context the `position:fixed` modal escapes (Chrome), so
// no z-index there can lift its frames above the popup — the bug ENG-30907. Page content keeps
// using the doc-wrapper overlay and stays correctly behind an open popup. The popup layer is
// still inside `.builder-doc-wrapper`, so the doc-wrapper Stimulus controller remains an
// ancestor and the toolbar/level actions keep resolving.
export const controlsOverlayFor = (node) => {
  const popupLayer = node
    .closest('.builder-popup')
    ?.querySelector('.t-modal-content-outer')
  if (!popupLayer) return getControlsOverlay(node.ownerDocument)

  return (
    existingOverlay(popupLayer) ||
    // z-index:1 lifts this overlay above the popup's `.t-modal-content` (isolation: isolate)
    // within the shared fixed modal layer, so popup-content frames paint over the popup content.
    makeOverlay(popupLayer, 'position:absolute;top:0;left:0;z-index:1;')
  )
}

// The popup the editor currently has open, if any. At most one is open at a time, and while it is
// its `position:fixed` backdrop (z-99999) captures every pointer event over the page — so it owns
// both the cursor-driven chrome (activeControlsOverlay) and the band hit-testing (sectionAtBandPoint).
// Exported so callers can tell whether the page's own `.builder-header` is even relevant right now —
// see headerBottom below and its use in proximity_adder_component_controller.js.
export const openPopupIn = (doc) =>
  doc.querySelector('.js-popup-container:not(.hidden) .builder-popup')

// The bottom edge (client coords) of the page's Header Layout node, or `null` if there is none / it
// isn't laid out yet (a hidden pop-up subtree, e.g.). Shared by every piece of portaled chrome that
// must stay clear of the header band: node_visual_controls_component_controller.js's per-node clip,
// level_selector_component_controller.js's pill flip, and proximity_adder_component_controller.js's
// insertion-pill floor — three independent copies of this lookup existed before this one (ENG-31837).
//
// Deliberately `null`, not `0`: `0` is a real, meaningful client-space coordinate (a header scrolled
// exactly to where its bottom edge sits at the viewport top), so it cannot also mean "no header
// here." `proximity_adder_component_controller.js`'s consumer tells the two apart with
// `env.headerBottom != null` — collapsing this to `0` would make an ordinary header-less page
// silently pick up a stray (if currently inert) floor value. `Math.max(x, null)` in the other two
// consumers below is unaffected either way — `null` coerces to `0` there exactly like the old
// literal `0` did.
export const headerBottom = (doc) => {
  const header = doc.querySelector('.builder-header')
  return header?.getClientRects().length
    ? header.getBoundingClientRect().bottom
    : null
}

// The overlay for whole-doc, cursor-driven chrome (the level selector pills and the proximity
// adder) — as opposed to a single node's frame, which uses controlsOverlayFor(node). The editor
// shows at most one popup at a time, and while it's open its `position:fixed` backdrop (z-99999)
// captures pointer events over the page, so the ONLY content you can hover is the popup's. Route
// that chrome into the open popup's own modal-layer overlay so it paints above the popup (its
// z-300/320 would otherwise lose to the z-99999 modal from the page overlay — the bug ENG-30907);
// fall back to the page overlay when no popup is open.
export const activeControlsOverlay = (doc = document) => {
  const openPopup = openPopupIn(doc)
  return openPopup ? controlsOverlayFor(openPopup) : getControlsOverlay(doc)
}

// A section reads as a horizontal BAND of the page, so its editor frame always spans the full
// page width — even when the section's own box is narrowed by its Width / Max width settings
// (`--node-width` / `--node-max-width`), which would otherwise draw the frame as a floating box
// in the middle of the band. Returns null for every other node type: they keep their own box.
//
// The band is the section parent's CONTENT box — exactly where a 100%-wide section sits. So it's
// a no-op for the common full-width section (the band IS its box), it follows any padding the
// body carries, and it stays correct for a section inside a popup, where "full width" means the
// popup's width and not the page's.
export const fullBleedRect = (node) => {
  if (!node?.classList?.contains('builder-section')) return null

  const parent = node.parentElement
  if (!parent) return null

  const nodeRect = node.getBoundingClientRect()
  // A section with no layout box has nothing to band across — the `visibility` setting emits
  // `display: none` for a section hidden at the current breakpoint, and every coordinate then
  // reads 0. Fall back to its own empty box (what it was before there was a band): otherwise the
  // union below stretches a ZERO-HEIGHT frame across the whole page, drawing a hairline over the
  // canvas whenever the node tree highlights a hidden section — highlightNodeInIframe has no
  // visibility guard, so hovering its row in the tree is enough.
  if (!nodeRect.width && !nodeRect.height) return null

  const rect = parent.getBoundingClientRect()
  const style = parent.ownerDocument.defaultView.getComputedStyle(parent)

  // Widen-only: union the band with the section's own box. A section CAN overflow its parent — a
  // custom Width plus a Max width above the viewport stops `max-width` clamping it to 100% — and a
  // band narrower than the section would draw a frame that no longer encloses what it frames
  // (worse than no band at all). Taking the union also keeps the frame a superset of the node's
  // box, which is what lets the padding handles be inset back onto the section by a non-negative
  // amount.
  const left = Math.min(
    rect.left +
      parseFloat(style.borderLeftWidth) +
      parseFloat(style.paddingLeft),
    nodeRect.left
  )
  const right = Math.max(
    rect.right -
      parseFloat(style.borderRightWidth) -
      parseFloat(style.paddingRight),
    nodeRect.right
  )

  return { left, right, width: right - left }
}

// The section whose full-bleed BAND covers the client point (x, y) — i.e. the strip of empty
// parent space beside a narrowed section that the section's frame paints across (see
// fullBleedRect). Null when the point isn't over any band.
//
// Hover and click both hit-test against a node's own DOM box, so without this that band is dead
// space: the frame draws a band across the page, but the cursor over it resolves to whatever sits
// underneath — the body. That drops the section's hover the instant the cursor crosses out of its
// real box (the frame vanishes from under the cursor) and sends a click aimed at the visible frame
// to the body instead. Callers resolve the band back to the section the frame belongs to, so the
// hit area matches what's drawn.
//
// `x`/`y` are client coordinates in `doc`, matching getBoundingClientRect().
export const sectionAtBandPoint = (doc, x, y) => {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null

  // While a popup is open only its content is reachable, so scope the scan to it — otherwise
  // hovering the backdrop would resolve to a page section banding along behind the modal. With no
  // popup, scope to the EDITING wrapper so a layout's (non-editing, uneditable) sections never
  // band; see editingDocWrapper.
  const root = openPopupIn(doc) || editingDocWrapper(doc) || doc

  // Only sections that actually render a frame can have a band — the band exists to match what's
  // painted, so a section with no visual controls has nothing to match.
  for (const section of root.querySelectorAll(
    '.builder-section.js-builder-node-with-visual-controls'
  )) {
    const rect = section.getBoundingClientRect()
    if (y < rect.top || y >= rect.bottom) continue

    // Inside the section's own box the normal element hit-test already owns the point, and must
    // keep owning it: that's what makes hovering CONTENT resolve to the innermost node rather than
    // being swallowed by the section around it.
    if (x >= rect.left && x < rect.right) return null

    const band = fullBleedRect(section)
    if (band && x >= band.left && x < band.right) return section
  }

  return null
}

// The band section at (x, y), but only when that section may be CLICKED — the click-path variant.
//
// A band click lands on an ancestor of the section (the band is the ancestor's empty space), so the
// ancestor's handler is what runs, and retargeting it must not hand the section an editing path the
// server deliberately withheld: `clickable` in node_component.rb omits the `click` action entirely
// for a deleted global block (and for anything non-editable), and an omitted action can't refuse an
// event that never reached the node. `js-builder-node-clickable` publishes that predicate so the
// check stays a single source of truth instead of being re-derived here.
//
// Hover deliberately uses the ungated sectionAtBandPoint: hovering a non-clickable node already
// highlights it today, so the band matches the node's own box rather than inventing a new rule.
export const clickableSectionAtBandPoint = (doc, x, y) => {
  const section = sectionAtBandPoint(doc, x, y)
  return section?.classList.contains('js-builder-node-clickable')
    ? section
    : null
}

// The controls element links to its node via `data-controls-node-id` — NOT a bare
// `data-node-id`. A bare `data-node-id` on the (portaled) control would collide with
// the node in document-wide `[data-node-id=X]` lookups (system tests, SortableJS
// moveNode, helpers.getNodeInPreviewIframe), since the control no longer lives inside
// its node. Keeping `data-node-id` exclusively on real nodes/tree-rows keeps those
// lookups unambiguous.

// Live portaled controls, by node id, so getVisualControlEl is a map lookup instead of a
// document-wide attribute query. Maintained by usePortaledControls across the controller's
// connect/disconnect (see registerVisualControl), which is the only thing that portals a control.
//
// Ids can repeat — expand_from_global_block copies a block's children verbatim, ids and all, so
// placing one global block twice renders two controls carrying the same id (see nodeOf). Hence a
// Set per id and a fall-back to the query whenever it does not hold exactly one live answer: the
// query resolves duplicates in document order, and that must stay the answer.
const controlsByNodeId = new Map()

// Register a portaled control so it can be resolved by id. Idempotent — portalize() moves the
// element, which makes Stimulus disconnect and reconnect the controller, so this runs twice per
// node at boot.
export const registerVisualControl = (element) => {
  const id = element?.dataset?.controlsNodeId
  if (!id) return

  let controls = controlsByNodeId.get(id)
  if (!controls) controlsByNodeId.set(id, (controls = new Set()))
  controls.add(element)
}

// Drop a control from the index, from the controller's disconnect.
//
// A control still in the document is not gone — it was MOVED (portalize's reparent triggers
// exactly this disconnect, and the reconnect that follows re-registers the same element), so
// deregistering it there would empty the index for every node at boot.
export const releaseVisualControl = (element) => {
  const id = element?.dataset?.controlsNodeId
  if (!id || element.isConnected) return

  const controls = controlsByNodeId.get(id)
  if (!controls?.delete(element)) return
  if (!controls.size) controlsByNodeId.delete(id)
}

// Given a .js-builder-node, return its (portaled) visual-controls element — resolved
// by id rather than as a descendant.
//
// The index is the fast path and the document query is the answer of record: the index is only
// populated by the controllers running in the node's own document (so a caller in the parent
// frame, reaching in at the preview iframe, always takes the query), it can hold an element that
// has since been removed without a disconnect, and it deliberately declines duplicate ids. This
// runs once per drop-target node at dragstart — hundreds of document-wide queries in one
// synchronous handler on a large page — so the fast path is the point.
export const getVisualControlEl = (builderNode) => {
  const id = builderNode?.dataset?.nodeId
  if (!id) return id

  const controls = controlsByNodeId.get(id)
  if (controls?.size === 1) {
    const [element] = controls
    if (
      element.isConnected &&
      element.ownerDocument === builderNode.ownerDocument
    ) {
      return element
    }
  }

  return builderNode.ownerDocument.querySelector(
    `.js-node-visual-controls[data-controls-node-id="${id}"]`
  )
}

// Splide's loop copies (`.splide__slide--clone`) repeat every descendant id, and the copies of
// the last slides come first in the track. A document-order lookup then answers those slides
// with the copy that sits off the canvas. Skip copies. When every match is a copy, keep the
// first one: a node that only exists as a copy must not look deleted.
const insideSlideClone = (el) => !!el?.closest?.('.splide__slide--clone')

export const firstOutsideSlideClone = (root, selector) => {
  const first = root.querySelector(selector)
  if (!insideSlideClone(first)) return first

  for (const el of root.querySelectorAll(selector)) {
    if (!insideSlideClone(el)) return el
  }
  return first
}

// getElementById returns that same leading copy. A non-element (a test double, null) is
// returned unchanged — it has no clone to skip.
export const elementOutsideSlideClone = (el) => {
  if (!el || typeof el.closest !== 'function' || !insideSlideClone(el)) {
    return el
  }

  const id = el.id
  if (!id || typeof el.ownerDocument?.querySelectorAll !== 'function') return el

  const escape = globalThis.CSS?.escape
  const matches = el.ownerDocument.querySelectorAll(
    `#${escape ? escape(id) : id}`
  )
  for (const match of matches) {
    if (!insideSlideClone(match)) return match
  }
  return el
}

// A selection frame is position:absolute in the preview document. clip-path hides the
// part that paints outside the preview, but the layout box still widens scrollWidth,
// so the preview grows a horizontal scrollbar once a slide sits off to the side.
// A box that misses the preview entirely is not shown. A viewport we cannot measure
// (clientWidth and innerWidth both 0) is left shown.
export const boxOutsideHorizontal = (doc, box) => {
  const view = doc?.defaultView
  if (!view || !box) return false
  const clientWidth = doc.documentElement?.clientWidth
  const viewport = clientWidth > 0 ? clientWidth : view.innerWidth
  if (!(viewport > 0)) return false
  const width = box.width > 0 ? box.width : 0
  if (box.left >= viewport) return true
  return width > 0 && box.left + width <= 0
}

// The outline and the level pill both write through these so an off-screen box is
// parked at the origin. clip-path and display:none leave the layout box in place,
// and that box is what widens the preview's scrollWidth.
export const selectionFrameStyles = ({
  top,
  left,
  width,
  height,
  outside,
  clipPath,
}) =>
  outside
    ? {
        position: 'absolute',
        top: '0px',
        left: '0px',
        width: '0px',
        height: '0px',
        right: 'auto',
        bottom: 'auto',
        clipPath: '',
        display: 'none',
      }
    : {
        position: 'absolute',
        top: `${top}px`,
        left: `${left}px`,
        width: `${width}px`,
        height: `${height}px`,
        right: 'auto',
        bottom: 'auto',
        clipPath: clipPath || '',
        display: '',
      }

export const selectionPillStyles = ({ left, top, outside }) => ({
  left: outside ? '0px' : left,
  top: outside ? '0px' : top,
  display: outside ? 'none' : '',
})

// Given a visual-controls element, return its .js-builder-node — resolved by id,
// since the controls are portaled and `closest('.js-builder-node')` won't find it.
export const nodeOf = (visualControlEl) => {
  const id = visualControlEl?.dataset?.controlsNodeId
  if (!id) return id

  // Until it is portaled, the control is still rendered INSIDE its own node (node_wrapper puts it
  // there), so the answer is an ancestor walk rather than a document-wide attribute scan — which
  // is what portalize asks for, once per node at boot. Confirm the id matches before trusting it:
  // once portaled the control is out of its node, `closest` finds an unrelated node or nothing
  // (a pop-up's overlay sits inside the pop-up NODE), and we fall through to the real lookup.
  //
  // Node ids are not guaranteed unique within a document: expand_from_global_block copies the
  // block's children verbatim, ids and all (builder_doc/node.rb), so placing one global block
  // twice on a page renders both copies with the same ids. The document query answers such a
  // control with the FIRST match — the other instance's node — where the walk answers with the
  // node the control is actually inside. That is the better answer, and it is the reason to walk
  // first rather than merely a faster way to reach the same one.
  const own = visualControlEl.closest('.js-builder-node')
  if (own?.dataset.nodeId === id) return own

  return firstOutsideSlideClone(
    visualControlEl.ownerDocument,
    `.js-builder-node[data-node-id="${id}"]`
  )
}

// A node's width constraint and its `.js-builder-node-with-visual-controls` hover class live on
// the same wrapper element (node_wrapper_tag in node_component.rb), so a "boxed" (not-full-width)
// node's own box stops at its own edge — the empty gutter beside it belongs to whichever
// ancestor's content wrapper centers it (`content: { class: '...items-center' }`, used by Body,
// Section, Column, Slide, Popup). Plain `closest('.js-builder-node-with-visual-controls')`
// hit-testing there lands on that ancestor (or nothing, if the ancestor isn't itself hoverable —
// e.g. Body) instead of the child the user is visually pointing at.
//
// Given the real hover event's target and clientY, find the nearest such centered content
// wrapper and return whichever of its direct children is hoverable, spans the cursor's Y, and
// does NOT already contain `target` (that would be a legitimate direct hover `closest()` already
// resolved correctly) — or `null` if none applies.
export const getGutterHoverNode = (target, clientY) => {
  const contentWrapper = target?.closest?.('.js-node-content.items-center')
  if (!contentWrapper) return null

  return (
    Array.from(contentWrapper.children).find((child) => {
      if (!child.classList?.contains('js-builder-node-with-visual-controls'))
        return false
      if (child.contains(target)) return false

      const rect = child.getBoundingClientRect()
      return clientY >= rect.top && clientY < rect.bottom
    }) || null
  )
}

// Number of .js-builder-node ancestors — used to order portaled controls so a
// descendant's controls paint above its ancestors' (ancestors inserted first).
const nodeDepth = (node) => {
  let depth = 0
  let el = node?.parentElement
  while (el) {
    if (el.classList?.contains('js-builder-node')) depth++
    el = el.parentElement
  }
  return depth
}

// Node ids that have had a control portaled into each overlay. Ids only, never elements — this
// must not be able to hold a removed control (and the subtree behind it) alive — and keyed weakly
// by the overlay so it dies with it.
const portaledNodeIds = new WeakMap()

// Insert `element` among the overlay's portaled controls, which are kept sorted by node depth so
// that a descendant's controls always paint above its ancestors'.
//
// `hi` first walks back over the overlay's trailing children that carry no depth: the
// level-selector pills, the proximity adder and its preview append themselves to the same overlay,
// and would read as depth NaN and corrupt the search. That leaves the portaled prefix, which is
// sorted, so the position is a binary search rather than a linear scan over everything already
// portaled. It reads the live DOM rather than a cached index, so it cannot go stale when a control
// leaves the overlay via a delete, a re-render or sweepOrphanedControls.
//
// With nothing deeper already portaled the element lands at the END of that prefix — ahead of the
// pills/adder rather than after them, which is where the old linear scan left it. Paint order is
// unaffected: that chrome is z-320 against the frames' z-300, so it sits above them either way.
// Keeping the controls a strict prefix is what lets this stay a binary search.
const insertInDepthOrder = (overlay, element, depth) => {
  const children = overlay.children

  let hi = children.length
  while (hi > 0 && children[hi - 1].dataset.portalDepth === undefined) hi--

  let lo = 0
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (Number(children[mid].dataset.portalDepth) > depth) hi = mid
    else lo = mid + 1
  }

  overlay.insertBefore(element, children[lo] || null)
}

// Move a controls element into the overlay, inserted in node-depth order so a
// descendant's controls always paint above its ancestors'. Drops any stale controls
// left in the overlay for the same node (e.g. from a re-render while it was shown).
//
// O(1) on the boot path, because boot calls this once per node and BOTH halves used to walk
// everything already portaled — a querySelectorAll for the stale controls and a linear scan over a
// materialised copy of the children for the position, N^2/2 between them and the largest
// superlinear term in editor boot. A node being portaled for the first time cannot have stale
// controls, so the id set skips that scan outright; the position is a binary search.
export const portalize = (element) => {
  // Answer the reconnect from the element alone, before any lookup. portalize() MOVES the element,
  // and Stimulus reacts to the move by disconnecting and reconnecting the controller (see the
  // lifecycle note in portaled_controls.js), so usePortaledControls calls this exactly twice per
  // node at boot and the second call has nothing to do. It used to reach that conclusion only
  // after resolving the node and the overlay — the two most expensive things in the function.
  //
  // A control already sitting in an overlay is in the right one: the only way to want a different
  // overlay is for its node to move in or out of a pop-up, and that goes through a server
  // re-render, which mints a fresh control element that is not in any overlay yet.
  if (element.parentElement?.classList.contains(OVERLAY_CLASS)) return

  const node = nodeOf(element)
  if (!node) return

  const overlay = controlsOverlayFor(node)

  let portaledIds = portaledNodeIds.get(overlay)
  if (!portaledIds) portaledNodeIds.set(overlay, (portaledIds = new Set()))

  const nodeId = node.dataset.nodeId
  if (portaledIds.has(nodeId)) {
    // Children only — see existingOverlay for why this is a loop and not a `:scope >` query. Every
    // control in the overlay carries a whole toolbar, so that query would walk all of it.
    for (const child of [...overlay.children]) {
      if (child !== element && child.dataset.controlsNodeId === nodeId) {
        child.remove()
      }
    }
  }
  portaledIds.add(nodeId)

  const depth = nodeDepth(node)
  element.dataset.portalDepth = depth
  insertInDepthOrder(overlay, element, depth)
}

// Remove portaled controls whose node no longer exists. A re-render replaces both the
// node and its controls (portalize de-dups the old controls), but a plain DELETE removes
// only the node — its controls element stays in the overlay (it's no longer a descendant
// of the node, so it gets no Stimulus disconnect). Removing it here fires that disconnect
// (→ teardown of its observer + tracking). Safe to run anytime: it only touches controls
// whose data-node-id resolves to no live node; a node being re-rendered still resolves
// (the fresh node carries the same id), so live controls are never swept.
export const sweepOrphanedControls = (doc = document) => {
  doc
    .querySelectorAll(`.${OVERLAY_CLASS} > .js-node-visual-controls`)
    .forEach((el) => nodeOf(el) || el.remove())
}

// The ids of a node and every node inside it. Read before a delete, while the subtree is still on
// the canvas, so its controls can be removed with it (see removeControlsFor).
export const nodeAndDescendantIds = (doc, nodeId) => {
  const node = firstOutsideSlideClone(doc, `[data-node-id="${nodeId}"]`)
  const descendants = node
    ? [...node.querySelectorAll('[data-node-id]')].map((el) => el.dataset.nodeId)
    : []

  return [nodeId, ...descendants]
}

// Remove the portaled controls of the given nodes now. sweepOrphanedControls only runs when a node
// disconnects, and a DELETE that is not a re-render leaves the pills on screen until the pointer
// moves over the canvas, where they would still take clicks.
export const removeControlsFor = (doc, nodeIds) => {
  doc
    .querySelectorAll(`.${OVERLAY_CLASS} > .js-node-visual-controls`)
    .forEach((el) => {
      if (nodeIds.includes(el.dataset.controlsNodeId)) el.remove()
    })

  // A level-selector pill has no node id of its own; its segments do, so a pill goes with any node it shows.
  doc.querySelectorAll(`.${OVERLAY_CLASS} > .js-level-pill`).forEach((pill) => {
    const segmentIds = [...pill.querySelectorAll('.js-level-segment[data-node-id]')].map((seg) => seg.dataset.nodeId)
    if (segmentIds.some((id) => nodeIds.includes(id))) pill.remove()
  })
}
